import React, { useEffect, useState, useRef, useCallback } from 'react';
import { forceCollide, forceManyBody } from 'd3-force';
import ForceGraph2D from 'react-force-graph-2d';
import html2canvas from 'html2canvas';
import { getItemColor } from '../../../config/oncorisk-config';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import './OncoRiskNetworkGraph.css';
import ReactDOM from 'react-dom';

library.add(fas);

// ======================== ADDITION START ========================
const toTitleCase = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};
// ======================== ADDITION END ========================

function getItemIcon(itemType) {
  const defaultIcon = 'circle';
  if (!itemType) return defaultIcon;

  let normalized = itemType.toLowerCase();

  if (normalized.endsWith('ies')) {
    normalized = normalized.slice(0, -3) + 'y';
  } else if (normalized.endsWith('s')) {
    normalized = normalized.slice(0, -1);
  }

  const iconMap = {
    'feature': 'puzzle-piece',
    'disease': 'heartbeat', 
    'therapy': 'pills',
    'assertion': 'check-circle',
    'evidence': 'file-alt',
    'variant': 'random',
    'molecularprofile': 'dna',
    'phenotype': 'user-tag',
    'source': 'database',
    'tissue': 'microscope' // <-- ADDITION
  };

  return iconMap[normalized] || defaultIcon;
}

const OncoRiskNetworkGraph = ({ data, diseaseTreeData, onNodeClick, focusedNodeId, isLightTheme }) => {
  const fgRef = useRef();
  const containerRef = useRef();
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [nodeCounts, setNodeCounts] = useState({}); // <-- ADDITION
  const [bouncingNodeId, setBouncingNodeId] = useState(null);
  const [bounceStartTime, setBounceStartTime] = useState(null);
  const [hasInitiallyFitted, setHasInitiallyFitted] = useState(false);
  const [isEngineRunning, setIsEngineRunning] = useState(false);
  
  const [nodeFilters, setNodeFilters] = useState({
    features: true,
    diseases: true,
    therapies: true,
    variants: true,
    molecularprofiles: true,
    assertions: true,
    evidences: true,
    phenotypes: true,
    sources: false,
    tissues: true // <-- ADDITION
  });

  const handleSnapshot = async () => {
    if (!containerRef.current) return;
    try {
      const canvas = await html2canvas(containerRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        scale: 2,
        logging: false,
        ignoreElements: (element) => element.classList.contains('ONCRP-TOOLS-QP-snapshot-btn')
      });
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `network_graph_${new Date().toISOString().slice(0,10)}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Failed to capture network graph:', e);
    }
  };

  useEffect(() => {
    if (focusedNodeId && fgRef.current && graphData.nodes.length > 0) {
      const node = graphData.nodes.find(n => n.originalId === focusedNodeId);
      if (node) {
        // Check if node has valid coordinates (force simulation has positioned it)
        const attemptFocus = () => {
          if (node.x !== undefined && node.y !== undefined && !isNaN(node.x) && !isNaN(node.y)) {
            fgRef.current.centerAt(node.x, node.y, 500); // 500ms transition
            fgRef.current.zoom(2.5, 500); // Zoom to level 2.5 in 500ms
            
            // Trigger bounce animation
            const startTime = Date.now();
            setBouncingNodeId(node.id);
            setBounceStartTime(startTime);
            
            // Start animation loop
            const animationLoop = () => {
              if (Date.now() - startTime < 800) {
                requestAnimationFrame(animationLoop);
              }
            };
            requestAnimationFrame(animationLoop);
            
            setTimeout(() => {
              setBouncingNodeId(null);
              setBounceStartTime(null);
            }, 800); // Bounce for 800ms
          } else {
            // Node doesn't have coordinates yet, wait a bit for force simulation
            setTimeout(attemptFocus, 100);
          }
        };
        
        attemptFocus();
      }
    }
  }, [focusedNodeId, graphData.nodes]);

  const toggleNodeFilter = useCallback((nodeType) => {
    setNodeFilters(prev => ({ ...prev, [nodeType]: !prev[nodeType] }));
  }, []);

  const getFilterColor = useCallback((nodeType) => {
    let colorType = nodeType.toLowerCase();
    if (colorType.endsWith('ies')) {
      colorType = colorType.slice(0, -3) + 'y';
    } else if (colorType.endsWith('s')) {
      colorType = colorType.slice(0, -1);
    }
    return getItemColor(colorType);
  }, []);

  const processData = useCallback((inputData, treeData) => { // <-- ADDED ARGUMENT
    if (!inputData || !inputData.length) {
      setGraphData({ nodes: [], links: [] });
      setNodeCounts({}); // <-- ADDITION
      return;
    }

    const nodes = [];
    const links = [];
    const nodeMap = {};
    const connectionCounts = {};

    const addNode = (id, name, type, isSearchResult = false, chipIndex = null) => {
      const nodeKey = `${type}-${id}`;
      if (!nodeMap[nodeKey]) {
        let colorType;
        if (type.endsWith('ies')) {
          colorType = type.slice(0, -3) + 'y';
        } else if (type.endsWith('s')) {
          colorType = type.slice(0, -1);
        } else {
          colorType = type;
        }
        
        const node = {
          id: nodeKey, originalId: id, name: name || id, type,
          color: getItemColor(colorType), icon: getItemIcon(colorType),
          size: type === 'features' ? 22 : 8, originalType: type,
          isMainNode: type === 'features', isSearchResult: isSearchResult,
          chipIndex: chipIndex
        };
        nodes.push(node);
        nodeMap[nodeKey] = node;
        connectionCounts[nodeKey] = 0;
      }
      return nodeMap[nodeKey];
    };

    inputData.forEach(item => {
      const isSearchResult = Boolean(item.chipIndex);
      const mainNode = addNode(item.id, item.name, item.type, isSearchResult, item.chipIndex);
      
      if (item.chipIndex) {
        if (mainNode.originalType !== 'features') {
          mainNode.size = 18;
          mainNode.isMainNode = true;
        }
      }
      
      const connectionsToProcess = [
        { key: 'associated_diseases', type: 'diseases' }, { key: 'associated_therapies', type: 'therapies' },
        { key: 'associated_assertions', type: 'assertions' }, { key: 'associated_evidence', type: 'evidences' },
        { key: '_evidence_items', type: 'evidences' }, { key: 'evidence_items', type: 'evidences' },
        { key: 'associated_phenotypes', type: 'phenotypes' }, { key: 'associated_sources', type: 'sources' },
        { key: '_sources', type: 'sources' }, { key: '_molecular_profiles', type: 'molecularprofiles' },
        { key: 'variants', type: 'variants' }
      ];

      connectionsToProcess.forEach(conn => {
        const associations = item[conn.key];
        if (!associations || !Array.isArray(associations)) return;
        associations.forEach(assocItem => {
            if (!assocItem || assocItem.id === undefined) return;
            const source = mainNode;
            const target = addNode(assocItem.id, assocItem.name, conn.type, false, null);
            connectionCounts[source.id] = (connectionCounts[source.id] || 0) + 1;
            connectionCounts[target.id] = (connectionCounts[target.id] || 0) + 1;
            links.push({ source: source.id, target: target.id, id: `${source.id}-${target.id}` });
        });
      });
    });

    // ======================== ADDITION START ========================
    if (treeData) {
      const diseaseNodesInGraph = nodes.filter(n => n.originalType === 'diseases');
      diseaseNodesInGraph.forEach(diseaseNode => {
          const tree = treeData[diseaseNode.name];
          const tissueName = tree?.mapping_result?.matched_oncotree_node?.tissue;
          if (tissueName) {
              const formattedTissueName = toTitleCase(tissueName);
              const tissueNode = addNode(tissueName, formattedTissueName, 'tissues', false, null);
              links.push({ source: diseaseNode.id, target: tissueNode.id, id: `${diseaseNode.id}-${tissueNode.id}` });
              connectionCounts[diseaseNode.id] = (connectionCounts[diseaseNode.id] || 0) + 1;
              connectionCounts[tissueNode.id] = (connectionCounts[tissueNode.id] || 0) + 1;
          }
      });
    }

    const counts = {};
    nodes.forEach(node => {
        const type = node.originalType.toLowerCase();
        counts[type] = (counts[type] || 0) + 1;
    });
    setNodeCounts(counts);
    // ======================== ADDITION END ========================

    nodes.forEach(node => {
      if (node.originalType !== 'features' && !node.isSearchResult) {
        const connectionCount = connectionCounts[node.id] || 0;
        if (connectionCount > 1) {
          const additionalSize = Math.min((connectionCount - 1) * 3, 12);
          node.size = 8 + additionalSize;
        }
      }
    });

    const filteredNodes = nodes.filter(node => {
      const filterKey = node.originalType.toLowerCase();
      // Map some types to filter keys
      const typeMapping = {
        'therapy': 'therapies', 'variant': 'variants', 'disease': 'diseases',
        'feature': 'features', 'molecularprofile': 'molecularprofiles',
        'assertion': 'assertions', 'evidence': 'evidences', 'phenotype': 'phenotypes',
        'source': 'sources', 'tissue': 'tissues'
      };
      const mappedType = typeMapping[filterKey] || filterKey;
      return nodeFilters[mappedType] !== false;
    });

    const filteredNodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredLinks = links.filter(link => 
      filteredNodeIds.has(link.source) && filteredNodeIds.has(link.target)
    );

    setGraphData({ nodes: filteredNodes, links: filteredLinks });
  }, [nodeFilters]);

  useEffect(() => {
    processData(data, diseaseTreeData); // <-- PASS ARGUMENT
    // Reset fitting state when data changes significantly
    if (data && data.length > 0) {
      setHasInitiallyFitted(false);
    }
  }, [data, diseaseTreeData, processData]); // <-- ADDED DEPENDENCY

  useEffect(() => {
    const observerTarget = containerRef.current;
    const resizeObserverCallback = (entries) => {
      if (!entries || !entries.length) return;
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    };
    const resizeObserver = new ResizeObserver(resizeObserverCallback);
    if (observerTarget) {
      resizeObserver.observe(observerTarget);
    }
    const handleWindowResize = () => {
      if (observerTarget) {
        setDimensions({
          width: observerTarget.offsetWidth,
          height: observerTarget.offsetHeight,
        });
      }
    };
    window.addEventListener('resize', handleWindowResize);
    return () => {
      if (observerTarget) {
        resizeObserver.unobserve(observerTarget);
      }
      window.removeEventListener('resize', handleWindowResize);
      resizeObserver.disconnect();
    };
  }, [data]);

  useEffect(() => {
    if (fgRef.current) {
      const chargeForce = forceManyBody().strength(-100);
      fgRef.current.d3Force('charge', chargeForce);
      const collideForce = forceCollide(node => node.size + 5).strength(1);
      fgRef.current.d3Force('collide', collideForce);
    }
    
    // Only do initial zoom to fit once, and only for significant data changes
    if (fgRef.current && graphData.nodes.length > 0 && dimensions.width > 0 && dimensions.height > 0 && !hasInitiallyFitted) {
      setTimeout(() => {
        if (fgRef.current && !hasInitiallyFitted) {
          fgRef.current.zoomToFit(400, 100);
          setHasInitiallyFitted(true);
        }
      }, 300); // Slightly longer delay to let nodes settle
    }
  }, [graphData.nodes.length, dimensions, hasInitiallyFitted]);

  if (!data || data.length === 0) {
    return <div className="ocrp-network-placeholder"></div>;
  }
  
  return (
    <div ref={containerRef} className="ocrp-network-container ocrp-network-grid">
      <button className="ONCRP-TOOLS-QP-snapshot-btn" style={{ left: 'auto', right: '1vh', top: '1vh' }} onClick={handleSnapshot} title="Save as PNG">
        <i className="fas fa-camera"></i>
        <span>Snapshot</span>
      </button>
      {dimensions.width > 0 && dimensions.height > 0 && (
        <ForceGraph2D
        ref={fgRef}
        width={dimensions.width}
        height={dimensions.height}
        graphData={graphData}
        nodeLabel="name"
        nodeVal="size"
        nodeCanvasObjectMode={(node) => node.__hover ? 'after' : 'after'}
                 nodeCanvasObject={(node, ctx, globalScale) => {
           const isBouncing = node.id === bouncingNodeId;
           let bounceScale = 1;
           
           if (isBouncing && bounceStartTime) {
             const elapsed = Date.now() - bounceStartTime;
             const progress = Math.min(elapsed / 800, 1); // 800ms total duration
             
             // Create a smooth bounce effect using sine wave
             const bounceAmount = Math.sin(progress * Math.PI * 3) * Math.exp(-progress * 3);
             bounceScale = 1 + bounceAmount * 0.3; // Scale between 1.0 and 1.3
           }
           
           const size = node.size * bounceScale;

           if (node.__hover) {
             ctx.beginPath();
             ctx.arc(node.x, node.y, size + 3, 0, 2 * Math.PI, false);
             ctx.fillStyle = isLightTheme ? 'rgba(37, 99, 235, 0.15)' : 'rgba(255, 255, 255, 0.2)';
             ctx.fill();
           }
           ctx.beginPath();
           ctx.arc(node.x, node.y, size + 1, 0, 2 * Math.PI, false);
           ctx.fillStyle = node.color;
           ctx.fill();
           ctx.fillStyle = isLightTheme ? '#1f2937' : 'white';
           ctx.textAlign = 'center';
           ctx.textBaseline = 'middle';
           if (!(node.isSearchResult && node.chipIndex)) {
             let symbol;
             switch (node.icon) {
               case 'heartbeat': symbol = '❤'; break;  
               case 'pills': symbol = '⊕'; break;        
               case 'check-circle': symbol = '✓'; break;  
               case 'file-alt': symbol = '⧠'; break;     
               case 'random': symbol = '⋏'; break;
               case 'dna': symbol = '⟠'; break;
               case 'user-tag': symbol = '⊗'; break;
               case 'database': symbol = '◉'; break;
               case 'microscope': symbol = '⚲'; break; // <-- ADDITION
               default: symbol = ' '; break;
             }
             const iconSize = Math.min(size * 0.45, 10);
             ctx.font = `${iconSize}px Arial`;
             ctx.fillText(symbol, node.x, node.y - size * 0.65);
           }
           if (node.isSearchResult && node.chipIndex) {
             const indexFontSize = Math.min(size * 0.25, 10);
             ctx.font = `bold ${indexFontSize}px Arial`;
             ctx.fillStyle = isLightTheme ? '#1f2937' : 'white';
             ctx.fillText(`#${node.chipIndex}`, node.x, node.y - size * 0.5);
           }
           if (node.isMainNode) {
             const displayName = node.name && node.name.length > 6 ? 
               node.name.substring(0, 6) + '...' : 
               node.name;
             const fontSize = Math.min(size * 0.3, 14);
             ctx.font = `bold ${fontSize}px Arial`;
             ctx.fillText(displayName, node.x, node.y);
           } 
           else {
             let shortLabel;
             if (node.name && node.name.length > 0) {
               shortLabel = node.name.substring(0, Math.min(2, node.name.length));
             } else {
               shortLabel = '?';
             }
             const fontSize = Math.min(size * 0.7, 14);
             ctx.font = `bold ${fontSize}px Arial`;
             ctx.fillText(shortLabel.toUpperCase(), node.x, node.y);
             const fontSize1 = node.name.length < 12 ? size * 0.35 : Math.min(size * 0.25, 11);
             ctx.font = `bold ${fontSize1}px Arial`;
             ctx.fillText(
               node.name.length > 12 ? node.name.slice(0, 12) + '..' : node.name,
               node.x,
               node.y + Math.min(size * 0.5, 8)
             );
           }
         }}
        linkDirectionalParticles={1}
        linkDirectionalParticleWidth={2}
        linkWidth={1}
        linkColor={() => isLightTheme ? 'rgba(148, 163, 184, 0.4)' : 'rgba(255,255,255,0.2)'}
        onNodeClick={onNodeClick}
        cooldownTicks={100}
        onEngineStart={() => setIsEngineRunning(true)}
        onEngineStop={() => {
          setIsEngineRunning(false);
          // Only zoom to fit on engine stop if we haven't done the initial fit yet
          // This prevents the camera from going crazy when tissues are added
          if (fgRef.current && dimensions.width > 0 && dimensions.height > 0 && !hasInitiallyFitted) {
            setTimeout(() => {
              if (fgRef.current && !hasInitiallyFitted) {
                fgRef.current.zoomToFit(400, 100);
                setHasInitiallyFitted(true);
              }
            }, 100);
          }
        }}
      />
      )}
      <div className="ocrp-network-filters">
        <div className="ocrp-filter-title">Show:</div>
        {Object.entries(nodeFilters).map(([nodeType, isVisible]) => (
          <label key={nodeType} className="ocrp-filter-toggle">
            <input
              type="checkbox"
              checked={isVisible}
              onChange={() => toggleNodeFilter(nodeType)}
            />
            <span 
              className="ocrp-filter-color-indicator"
              style={{ backgroundColor: getFilterColor(nodeType) }}
            ></span>
            <span className="ocrp-filter-label">
              {
                {
                  molecularprofiles: 'Profiles', evidences: 'Evidence', therapies: 'Therapies',
                  diseases: 'Diseases', variants: 'Variants', assertions: 'Assertions',
                  phenotypes: 'Phenotypes', sources: 'Sources', features: 'Features',
                  tissues: 'Tissues' // <-- ADDITION
                }[nodeType] || nodeType.charAt(0).toUpperCase() + nodeType.slice(1)
              }
              {/*// ======================== ADDITION START ========================*/}
              <span className="ocrp-filter-count">
                ({nodeCounts[nodeType] || 0})
              </span>
              {/*// ======================== ADDITION END ========================*/}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default OncoRiskNetworkGraph;